export const PICKUP_AND_DROPOFF_FULFILMENT_METHOD = 'PICKUP_AND_DROPOFF';

export interface PickupAndDropoffCoordinateState {
  latitude?: number | null;
  longitude?: number | null;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
}

export interface PickupAndDropoffAddressState
  extends PickupAndDropoffCoordinateState {
  customerStreet?: unknown;
  pickupStreet?: unknown;
}

export interface AssociatedZoneAddressState
  extends PickupAndDropoffAddressState {
  deliveryMethod?: unknown;
  customerTown?: unknown;
  customerZip?: unknown;
  addressId?: unknown;
  pickupTown?: unknown;
  pickupZip?: unknown;
  pickupAddressId?: unknown;
}

export function buildAssociatedZoneAddressKey(
  address: AssociatedZoneAddressState | null | undefined,
  orderFulfilmentMethod: string | null | undefined,
): string {
  const fields: (keyof AssociatedZoneAddressState)[] = [
    'customerStreet',
    'customerTown',
    'customerZip',
    'addressId',
    'latitude',
    'longitude',
  ];
  if (isPickupAndDropoffFulfilmentMethod(orderFulfilmentMethod)) {
    fields.push(
      'pickupStreet',
      'pickupTown',
      'pickupZip',
      'pickupAddressId',
      'pickupLatitude',
      'pickupLongitude',
    );
  }
  return JSON.stringify([
    address?.deliveryMethod,
    orderFulfilmentMethod,
    ...fields.map((field) => address?.[field]),
  ]);
}

export function isPickupAndDropoffFulfilmentMethod(
  orderFulfilmentMethod: string | null | undefined,
): boolean {
  return orderFulfilmentMethod === PICKUP_AND_DROPOFF_FULFILMENT_METHOD;
}

export function hasPickupAndDropoffStreetValues(
  addressState: Pick<
    PickupAndDropoffAddressState,
    'customerStreet' | 'pickupStreet'
  >,
): boolean {
  return (
    hasNonBlankText(addressState.pickupStreet) &&
    hasNonBlankText(addressState.customerStreet)
  );
}

export function hasCompletePickupAndDropoffCoordinates(
  coordinateState: PickupAndDropoffCoordinateState | null | undefined,
): boolean {
  return (
    hasCoordinatePair(
      coordinateState?.pickupLatitude,
      coordinateState?.pickupLongitude,
    ) &&
    hasCoordinatePair(coordinateState?.latitude, coordinateState?.longitude)
  );
}

export function hasCompletePickupAndDropoffAddressState(
  addressState: PickupAndDropoffAddressState | null | undefined,
): boolean {
  if (!addressState) {
    return false;
  }
  return (
    hasPickupAndDropoffStreetValues(addressState) &&
    hasCompletePickupAndDropoffCoordinates(addressState)
  );
}

export function shouldDispatchAssociatedZoneLookup(
  orderUpdateType: string | null | undefined,
  orderFulfilmentMethod: string | null | undefined,
  coordinateState: PickupAndDropoffCoordinateState | null | undefined,
): boolean {
  if (!isAssociatedZoneLookupTrigger(orderUpdateType)) {
    return false;
  }
  if (!isPickupAndDropoffFulfilmentMethod(orderFulfilmentMethod)) {
    return true;
  }
  return hasCompletePickupAndDropoffCoordinates(coordinateState);
}

/**
 * A zone verdict must use the confirmed coordinates after they reach the cart.
 * Missing state objects are not ready. An absent coordinate pair needs no wait
 * (for example, postcode-only delivery), but a partial or invalid pair is not ready.
 * Compare confirmed points at the backend's six-decimal storage precision.
 * Pickup coordinates matter only for pickup-and-dropoff orders.
 */
export function hasPersistedZoneCoordinates(
  currentCoordinates: PickupAndDropoffCoordinateState | null | undefined,
  persistedCoordinates: PickupAndDropoffCoordinateState | null | undefined,
  orderFulfilmentMethod: string | null | undefined,
): boolean {
  if (!currentCoordinates || !persistedCoordinates) {
    return false;
  }
  return (
    hasPersistedCoordinatePair(
      currentCoordinates.latitude,
      currentCoordinates.longitude,
      persistedCoordinates.latitude,
      persistedCoordinates.longitude,
    ) &&
    (!isPickupAndDropoffFulfilmentMethod(orderFulfilmentMethod) ||
      hasPersistedCoordinatePair(
        currentCoordinates.pickupLatitude,
        currentCoordinates.pickupLongitude,
        persistedCoordinates.pickupLatitude,
        persistedCoordinates.pickupLongitude,
      ))
  );
}

function hasPersistedCoordinatePair(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  persistedLatitude: number | null | undefined,
  persistedLongitude: number | null | undefined,
): boolean {
  if (latitude == null && longitude == null) {
    return true;
  }
  return (
    hasCoordinatePair(latitude, longitude) &&
    hasCoordinatePair(persistedLatitude, persistedLongitude) &&
    matchesPersistedCoordinate(latitude, persistedLatitude) &&
    matchesPersistedCoordinate(longitude, persistedLongitude)
  );
}

function matchesPersistedCoordinate(
  current: number,
  persisted: number,
): boolean {
  // DECIMAL storage rounds by at most half a unit in its sixth decimal place.
  // Allow only that rounding plus the binary-number error at the boundary.
  const tolerance =
    0.0000005 +
    Number.EPSILON * Math.max(1, Math.abs(current), Math.abs(persisted));
  return Math.abs(current - persisted) <= tolerance;
}

function isAssociatedZoneLookupTrigger(
  orderUpdateType: string | null | undefined,
): boolean {
  return (
    orderUpdateType === 'UpdateZipCode' ||
    orderUpdateType === 'UpdateZoneLocation'
  );
}

function hasCoordinatePair(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  return isFiniteCoordinate(latitude) && isFiniteCoordinate(longitude);
}

function isFiniteCoordinate(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

function hasNonBlankText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}
